import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Flag, 
  MapPin, 
  PlusCircle, 
  Trash2 
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCourse, setShowNewCourse] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [newCoursePar, setNewCoursePar] = useState('72');
  const [newCourseLocation, setNewCourseLocation] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API}/courses`);
        setCourses(response.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error("Failed to load courses");
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const handleAddCourse = async () => {
    if (!newCourseName.trim()) {
      toast.error("Please enter a course name");
      return;
    }

    try {
      const response = await axios.post(`${API}/courses`, {
        name: newCourseName.trim(),
        par: Number(newCoursePar),
        location: newCourseLocation.trim() || null
      });
      setCourses([...courses, response.data]);
      setShowNewCourse(false);
      setNewCourseName('');
      setNewCoursePar('72');
      setNewCourseLocation('');
      toast.success("Course added!");
    } catch (error) {
      toast.error("Failed to add course");
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course?")) return;
    
    try {
      await axios.delete(`${API}/courses/${courseId}`);
      setCourses(courses.filter(c => c.course_id !== courseId));
      toast.success("Course deleted");
    } catch (error) {
      toast.error("Failed to delete course");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="courses-page">
      <div className="flex justify-center mb-6">
        <Dialog open={showNewCourse} onOpenChange={setShowNewCourse}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-course-btn">
              <PlusCircle className="w-5 h-5 mr-2" />
              Add Course
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Add New Course</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Course Name</Label>
                <Input
                  value={newCourseName}
                  onChange={(e) => setNewCourseName(e.target.value)}
                  placeholder="e.g., Pebble Beach"
                  data-testid="course-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Par</Label>
                <Input
                  type="number"
                  value={newCoursePar}
                  onChange={(e) => setNewCoursePar(e.target.value)}
                  placeholder="72"
                  data-testid="course-par-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Location (optional)</Label>
                <Input
                  value={newCourseLocation}
                  onChange={(e) => setNewCourseLocation(e.target.value)}
                  placeholder="e.g., California, USA"
                  data-testid="course-location-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleAddCourse} className="btn-primary" data-testid="save-course-btn">
                Add Course
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {courses.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <MapPin className="empty-state-icon" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No courses yet</h3>
            <p className="text-stone-500 mb-6">Add your favorite golf courses to track scores.</p>
            <Button onClick={() => setShowNewCourse(true)} className="btn-primary">
              Add Your First Course
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {courses.map((course) => (
            <Card key={course.course_id} className="group" data-testid={`course-card-${course.course_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Flag className="w-6 h-6 text-green-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900">{course.name}</h3>
                      <p className="text-sm text-stone-500">Par {course.par}</p>
                      {course.location && (
                        <p className="text-sm text-stone-400 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {course.location}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(course.course_id)}
                    className="opacity-0 group-hover:opacity-100 p-2 text-stone-400 hover:text-red-500 transition-all"
                    data-testid={`delete-course-${course.course_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoursesPage;
